<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import WootModal from 'dashboard/components/Modal.vue';
import ContactTypeahead from './ContactTypeahead.vue';

const props = defineProps({
  prefill: { type: Object, default: null },
});
const emit = defineEmits(['close', 'created']);

const store = useStore();
const { t } = useI18n();

const show = ref(true);
const activeTab = ref('basic');
const errorKeys = ref([]);

const fieldDefinitions = computed(
  () => store.getters['painelFieldDefinitions/getActiveFieldDefinitions']
);
const requiredKeys = computed(() =>
  store.getters['painelFieldDefinitions/getRequiredFieldDefinitions'].map(
    f => f.key
  )
);

const customFields = ref({});

watch(
  fieldDefinitions,
  defs => {
    const next = {};
    defs.forEach(d => {
      next[d.key] = customFields.value[d.key] || '';
    });
    customFields.value = next;
  },
  { immediate: true }
);

const form = ref({
  starts_at: props.prefill?.startsAt || '',
  duration_minutes: props.prefill?.duration || 30,
  contact_id: null,
  summary: '',
});

const saving = ref(false);

onMounted(() => {
  store.dispatch('painelFieldDefinitions/fetch');
});

const canSubmit = computed(() => !saving.value && !!form.value.starts_at);

const close = () => {
  show.value = false;
  emit('close');
};

const validateRequired = () => {
  errorKeys.value = requiredKeys.value.filter(
    k => !customFields.value[k] || !customFields.value[k].trim()
  );
  return errorKeys.value.length === 0;
};

const submit = async () => {
  if (!validateRequired()) {
    activeTab.value = 'details';
    return;
  }
  saving.value = true;
  try {
    const payload = { ...form.value, custom_fields: customFields.value };
    const created = await store.dispatch('painelAppointments/create', payload);
    emit('created', created);
    close();
  } catch (err) {
    saving.value = false;
    if (err?.response?.data?.code === 'calendar_conflict') {
      window.bus?.$emit?.(
        'newToastMessage',
        t('PAINEL.APPOINTMENTS.TOAST.CONFLICT')
      );
    } else {
      throw err;
    }
  }
};
</script>

<template>
  <WootModal v-model:show="show" @close="close">
    <div class="p-6">
      <h2 class="mb-4 text-lg font-semibold">
        {{ t('PAINEL.APPOINTMENTS.EDITOR.TITLE_NEW') }}
      </h2>

      <div class="mb-4 flex gap-0 border-b border-n-strong">
        <button
          type="button"
          class="border-b-2 px-4 py-2 text-sm"
          :class="
            activeTab === 'basic'
              ? 'border-n-brand font-semibold text-n-slate-12'
              : 'border-transparent text-n-slate-11'
          "
          @click="activeTab = 'basic'"
        >
          {{ t('PAINEL.EDITOR.TAB_BASIC') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 border-b-2 px-4 py-2 text-sm"
          :class="
            activeTab === 'details'
              ? 'border-n-brand font-semibold text-n-slate-12'
              : 'border-transparent text-n-slate-11'
          "
          @click="activeTab = 'details'"
        >
          {{ t('PAINEL.EDITOR.TAB_DETAILS') }}
          <span
            v-if="fieldDefinitions.length"
            class="ml-1 rounded-full bg-n-brand px-2 py-0.5 text-xs text-white"
            >{{ fieldDefinitions.length }}</span
          >
        </button>
      </div>

      <form class="space-y-4" @submit.prevent="submit">
        <div v-show="activeTab === 'basic'" class="space-y-4">
          <div class="block">
            <span class="text-sm">{{
              t('PAINEL.APPOINTMENTS.DETAIL.CONTACT')
            }}</span>
            <div class="mt-1">
              <ContactTypeahead
                v-model="form.contact_id"
                :placeholder="t('PAINEL.APPOINTMENTS.SEARCH_PLACEHOLDER')"
              />
            </div>
          </div>

          <label class="block">
            <span class="text-sm">{{
              t('PAINEL.APPOINTMENTS.EDITOR.START')
            }}</span>
            <input
              v-model="form.starts_at"
              type="datetime-local"
              required
              class="mt-1 w-full rounded border border-n-strong px-2 py-1"
            />
          </label>

          <label class="block">
            <span class="text-sm">{{
              t('PAINEL.APPOINTMENTS.EDITOR.DURATION')
            }}</span>
            <input
              v-model.number="form.duration_minutes"
              type="number"
              min="15"
              step="15"
              required
              class="mt-1 w-full rounded border border-n-strong px-2 py-1"
            />
          </label>

          <label class="block">
            <span class="text-sm">{{
              t('PAINEL.APPOINTMENTS.EDITOR.SUMMARY')
            }}</span>
            <input
              v-model="form.summary"
              type="text"
              class="mt-1 w-full rounded border border-n-strong px-2 py-1"
            />
          </label>
        </div>

        <div v-show="activeTab === 'details'" class="space-y-4">
          <div
            v-if="fieldDefinitions.length === 0"
            class="text-sm text-n-slate-11"
          >
            {{ t('PAINEL.EDITOR.TAB_DETAILS_EMPTY') }}
            <router-link
              :to="{ name: 'painel_configuracoes', query: { tab: 'fields' } }"
              class="ml-2 underline"
            >
              {{ t('PAINEL.EDITOR.TAB_DETAILS_EMPTY_LINK') }}
            </router-link>
          </div>
          <label v-for="def in fieldDefinitions" :key="def.id" class="block">
            <span class="text-sm">
              {{ def.label }}
              <span v-if="def.required" class="text-n-ruby-9">*</span>
            </span>
            <input
              v-if="def.field_type === 'single'"
              v-model="customFields[def.key]"
              type="text"
              :placeholder="def.placeholder"
              :class="
                errorKeys.includes(def.key)
                  ? 'border-n-ruby-9'
                  : 'border-n-strong'
              "
              class="mt-1 w-full rounded border px-2 py-1"
            />
            <textarea
              v-else
              v-model="customFields[def.key]"
              rows="3"
              :placeholder="def.placeholder"
              :class="
                errorKeys.includes(def.key)
                  ? 'border-n-ruby-9'
                  : 'border-n-strong'
              "
              class="mt-1 w-full rounded border px-2 py-1"
            />
          </label>
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <button
            type="button"
            class="rounded bg-n-slate-3 px-4 py-2 text-sm"
            @click="close"
          >
            {{ t('PAINEL.APPOINTMENTS.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="!canSubmit"
            class="rounded bg-n-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {{ t('PAINEL.APPOINTMENTS.EDITOR.SUBMIT') }}
          </button>
        </div>
      </form>
    </div>
  </WootModal>
</template>
